import { decodificar } from '@/features/captura/comprimirImagem';

/**
 * Checagem de qualidade da foto de leitura (placa/KM) antes de enviar.
 *
 * Lente embaçada e câmera ruim dão foto borrada; garagem sem luz dá foto
 * escura — e o OCR devolve "não li" sem dizer por quê. A medida é barata
 * (imagem reduzida a 512px) e o aviso nunca bloqueia: quem está vendo a
 * placa nítida na foto envia assim mesmo.
 *
 * Mesma regra, com os mesmos números, no JS da página do celular
 * (`server/src/captura/paginaCelular.ts`, `JS_QUALIDADE_FOTO`).
 */

/** Lado maior da imagem analisada. */
export const LADO_ANALISE = 512;

/**
 * Variância do Laplaciano (4 vizinhos, tons de cinza 0–255, imagem a 512px)
 * abaixo disto = borrada. Calibração (2026-10-19): foto real de placa à
 * noite, nítida, deu 66; placas sintéticas nítidas, 240–370. Com desfoque
 * gaussiano σ=2px (na escala de 512) caem para 2–10, e com véu de lente
 * embaçada (35–50% de cinza claro + σ 1,5–2,5), para 1–9. 15 deixa folga de
 * 4× para a foto nítida mais difícil e pega os desfoques que derrubam o OCR;
 * desfoque leve (σ=1) costuma passar, e o OCR ainda lê.
 */
export const LIMIAR_NITIDEZ = 15;

/**
 * Escura = média abaixo de 40 E o 1% mais claro abaixo de 70. Só a média
 * marcaria o painel do carro (fundo preto, dígitos acesos: média 19, p99
 * 195) e a placa à noite com flash (média 45, p99 128); exigir que nem a
 * parte mais clara tenha luz separa "noite com a placa iluminada" de
 * "não dá para ver nada" (mesma foto a 50% de brilho: média 22, p99 64).
 */
export const LIMIAR_ESCURA_MEDIA = 40;
export const LIMIAR_ESCURA_P99 = 70;

export type QualidadeFoto = {
  /** Variância do Laplaciano. */
  nitidez: number;
  /** Brilho médio, 0–255. */
  brilhoMedio: number;
  /** Brilho do percentil 99, 0–255. */
  brilhoP99: number;
};

export type AvisoQualidade = {
  tipo: 'escura' | 'borrada';
  titulo: string;
  mensagem: string;
};

/** Luma (BT.601) de um RGBA do canvas. */
export function cinzaDeRgba(rgba: ArrayLike<number>, largura: number, altura: number): Float32Array {
  const n = largura * altura;
  const cinza = new Float32Array(n);
  for (let i = 0; i < n; i++) {
    const j = i * 4;
    cinza[i] = 0.299 * rgba[j] + 0.587 * rgba[j + 1] + 0.114 * rgba[j + 2];
  }
  return cinza;
}

export function avaliarQualidade(cinza: ArrayLike<number>, largura: number, altura: number): QualidadeFoto {
  const n = largura * altura;
  if (n <= 0) return { nitidez: 0, brilhoMedio: 0, brilhoP99: 0 };

  let soma = 0;
  const histograma = new Uint32Array(256);
  for (let i = 0; i < n; i++) {
    const v = cinza[i];
    soma += v;
    histograma[Math.max(0, Math.min(255, Math.round(v)))]++;
  }
  const corte = Math.ceil(n * 0.99);
  let acumulado = 0;
  let p99 = 255;
  for (let b = 0; b < 256; b++) {
    acumulado += histograma[b];
    if (acumulado >= corte) {
      p99 = b;
      break;
    }
  }

  // Laplaciano só no miolo (a borda não tem os 4 vizinhos).
  let nitidez = 0;
  if (largura >= 3 && altura >= 3) {
    let s = 0;
    let s2 = 0;
    let k = 0;
    for (let y = 1; y < altura - 1; y++) {
      for (let x = 1; x < largura - 1; x++) {
        const i = y * largura + x;
        const l = 4 * cinza[i] - cinza[i - 1] - cinza[i + 1] - cinza[i - largura] - cinza[i + largura];
        s += l;
        s2 += l * l;
        k++;
      }
    }
    const media = s / k;
    nitidez = s2 / k - media * media;
  }
  return { nitidez, brilhoMedio: soma / n, brilhoP99: p99 };
}

/** O aviso a mostrar, ou null quando a foto está boa. Escura vence borrada. */
export function avisoDeQualidade(q: QualidadeFoto): AvisoQualidade | null {
  if (q.brilhoMedio < LIMIAR_ESCURA_MEDIA && q.brilhoP99 < LIMIAR_ESCURA_P99) {
    return {
      tipo: 'escura',
      titulo: 'Foto muito escura',
      mensagem: 'A foto está muito escura. Acenda a lanterna ou chegue mais perto da luz.',
    };
  }
  if (q.nitidez < LIMIAR_NITIDEZ) {
    return {
      tipo: 'borrada',
      titulo: 'Foto borrada',
      mensagem: 'A foto saiu borrada. Limpe a lente da câmera e tente de novo — ou envie assim mesmo.',
    };
  }
  return null;
}

/**
 * Mede a foto escolhida. `null` quando o aparelho não deixa medir (sem
 * canvas, formato que o navegador não abre…) — aí não se pergunta nada.
 */
export async function medirQualidadeFoto(arquivo: Blob): Promise<QualidadeFoto | null> {
  try {
    const img = await decodificar(arquivo);
    try {
      const escala = Math.min(1, LADO_ANALISE / Math.max(img.largura, img.altura));
      const largura = Math.max(1, Math.round(img.largura * escala));
      const altura = Math.max(1, Math.round(img.altura * escala));
      const canvas = document.createElement('canvas');
      canvas.width = largura;
      canvas.height = altura;
      const ctx = canvas.getContext('2d', { willReadFrequently: true });
      if (!ctx) return null;
      ctx.imageSmoothingQuality = 'high';
      ctx.drawImage(img.fonte, 0, 0, largura, altura);
      const dados = ctx.getImageData(0, 0, largura, altura).data;
      return avaliarQualidade(cinzaDeRgba(dados, largura, altura), largura, altura);
    } finally {
      img.liberar();
    }
  } catch {
    return null;
  }
}
